/**
 * Checks if a value is required.
 * @param val - The value to check.
 * @returns True if the value is required, false otherwise.
 */
export const required = (val: string): boolean => {
    if (val === null || val === undefined) return false;
    return val.trim().length > 0;
};

/**
 * Checks if a value does not exceed the maximum length.
 * @param len - The maximum length.
 * @returns A function that checks if a value does not exceed the maximum length.
 */
export const maxLength =
    (len: number) =>
    (val: string): boolean => {
        if (val === null || val === undefined) return true;
        return val.length <= len;
    };

/**
 * Checks if a value meets the minimum length.
 * @param len - The minimum length.
 * @returns A function that checks if a value meets the minimum length.
 */
export const minLength =
    (len: number) =>
    (val: string): boolean => {
        if (val === null || val === undefined) return false;
        return val.length >= len;
    };

/**
 * Checks if a value is a number.
 * @param val - The value to check.
 * @returns True if the value is a number, false otherwise.
 */
export const isNumber = (val: string): boolean => {
    if (val === null || val === undefined || val === '') return false;
    return !isNaN(Number(val));
};

/**
 * Checks if a value matches a regular expression.
 * @param exp - The regular expression.
 * @returns A function that checks if a value matches the regular expression.
 */
export const validExpression =
    (exp: string) =>
    (val: string): boolean => {
        if (val === null || val === undefined) return false;
        return new RegExp(`^(?:${exp})$`, 'i').test(val);
    };

/**
 * Checks if a value is alphanumeric.
 * @param val - The value to check.
 * @returns True if the value is alphanumeric, false otherwise.
 */
export const validAlphanumeric = (val: string): boolean => {
    if (val === null || val === undefined) return false;
    return validExpression('[\u0660-\u0669\u0621-\u064A0-9 ]+|[a-zA-Z0-9 ]+')(val);
};

/**
 * Checks if a value is a valid email address.
 * @param val - The value to check.
 * @returns True if the value is a valid email address, false otherwise.
 */
export const validEmail = (val: string): boolean => {
    if (val === null || val === undefined) return false;
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(val);
};
